import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import _to_consumable_array from "@swc/helpers/src/_to_consumable_array.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import Link from "next/link";
import { Links } from "@silvertours/front-shared";
import { useEffect, useMemo, useRef, useState } from "react";
import { analytics } from "@silvertours/front-entities";
import { Anchor, LinkList, LinkListItem, MaskedLink, Nav, StyledText, StyledBadge } from "./Navigation.styles";
var makeLinkMapper = function(container, onClick) {
    return function(param) {
        var href = param.href, target = param.target, text = param.text, title = param.title, masked = param.masked, highlightText = param.highlightText;
        var linkProps = {
            href: href,
            target: target,
            title: title
        };
        var handleClick = function() {
            analytics.gtm.trackNavigationClicked(text);
            if (typeof onClick === "function") {
                onClick();
            }
        };
        return /*#__PURE__*/ _jsxs(LinkListItem, {
            container: container,
            onClick: handleClick,
            children: [
                /*#__PURE__*/ _jsx(StyledText, {
                    withPadding: container === "navbar",
                    children: masked ? /*#__PURE__*/ _jsx(MaskedLink, _object_spread_props(_object_spread({}, linkProps), {
                        body: text
                    })) : /*#__PURE__*/ _jsx(Link, {
                        href: href,
                        children: /*#__PURE__*/ _jsx(Anchor, _object_spread_props(_object_spread({}, linkProps), {
                            rel: linkProps.target === "_blank" ? "noopener noreferrer" : undefined,
                            children: text
                        }))
                    })
                }),
                highlightText && /*#__PURE__*/ _jsx(StyledBadge, {
                    text: highlightText,
                    type: "orange",
                    size: "small",
                    container: container
                })
            ]
        }, href);
    };
};
var Navigation = function(param) {
    var getHiddenElements = param.getHiddenElements, links = param.links, mode = param.mode, onAction = param.onAction;
    var getLinks = useMemo(function() {
        return Links.makeLinkTypeMapper(links);
    }, [
        links
    ]);
    var headerLinks = useMemo(function() {
        return getLinks("main");
    }, [
        getLinks
    ]);
    var linksRef = useRef(null);
    var ref = useState(headerLinks.length), visibleLinks = ref[0], setVisibleLinks = ref[1];
    var ref1 = useState([]), linkWidths = ref1[0], setLinkWidths = ref1[1];
    useEffect(function() {
        // Width of links component on first render
        var container = linksRef.current;
        if (!container) return undefined;
        // Create a ResizeObserver instance
        var resizeObserver = new ResizeObserver(function() {
            var linkElements = Array.from(container.children);
            setLinkWidths(linkElements.map(function(element) {
                return element.getBoundingClientRect().width;
            }));
        });
        // Attach the ResizeObserver to the container element
        resizeObserver.observe(container);
        // Cleanup function to disconnect the ResizeObserver when the component unmounts
        return function() {
            resizeObserver.disconnect();
        };
    }, []);
    useEffect(function() {
        if (!getHiddenElements || !linkWidths.length) {
            return;
        }
        var container = linksRef.current;
        if (!container) return;
        var totalWidth = 0;
        var visible = 0;
        _to_consumable_array(linkWidths).reverse().forEach(function(width) {
            totalWidth += width;
            if (totalWidth <= container.clientWidth) {
                visible += 1;
            }
        });
        setVisibleLinks(visible);
        var mapLinks = makeLinkMapper("popover", onAction);
        getHiddenElements(headerLinks.slice(0, headerLinks.length - visible).map(mapLinks));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        linkWidths,
        headerLinks
    ]);
    var mapLinks = makeLinkMapper("navbar", onAction);
    return /*#__PURE__*/ _jsx(Nav, {
        $mode: mode,
        "aria-label": "Main navigation",
        children: /*#__PURE__*/ _jsx(LinkList, {
            ref: linksRef,
            hideChildren: headerLinks.length - visibleLinks,
            children: headerLinks.map(mapLinks)
        })
    });
};
export { Navigation };
